/* eslint-disable @typescript-eslint/no-explicit-any */
import { SignalMapSetting } from '@deckee/api-app-main';
import { captureEvent } from '../../event-capture';
import { vesselIconSpeedBoat } from '../../../components/shared/vector-images';
import { getStore } from '../../store';
import { selectLocationConfig } from '../../store/modules/app-controls';
import { selectHasCurrentMemberActiveSubscription } from '../../store/modules/backend-service';
import { selectCurrentLocation } from '../../store/modules/geo-service';
import {
  getOnTheWaterFromState,
  getSignalForCurrentMember,
} from '../../store/modules/signal';
import { selectSelectedOrFirstVessel } from '../../store/modules/vessels/selectors';
import { telemetry } from '../../backend';
import { ensureBoatTopImages } from '../boat-icons/boat-icon-images';
import {
  boatIconForWatercraftType,
  boatIconLayout,
  normalizeHeading,
} from '../boat-icons/boat-icons';
import {
  registerBoatLayers,
  setBoatSourceData,
  tiltedBoatIconLayout,
  watchTiltedBoatView,
} from '../boat-icons/tilted-boats';
import {
  layerExists,
  setDataSource,
  sourceExists,
  toggleLayerIfRequired,
} from '../_helpers';
import { loadPinWrapImages } from '../pin-wrap-images';

let mapReference: any;
let lastT = 0;
let pulseCount = 0;

// eslint-disable-next-line no-shadow
enum PULSE_COLOR_CODES {
  BLUE = '35, 132, 211',
  GOLD = '199, 133, 41',
  ORANGE = '255, 92, 57',
}

const emptyStateDataSource = {
  type: 'geojson',
  data: {
    type: 'FeatureCollection',
    features: [],
  },
};

const pulsingDot = async (
  offsetX = 0,
  offsetY = 0,
  pulseSize = 200,
  pulseColor: PULSE_COLOR_CODES = PULSE_COLOR_CODES.BLUE,
) => ({
  width: pulseSize,
  height: pulseSize,
  data: new Uint8Array(pulseSize * pulseSize * 4),

  // When the layer is added to the map,
  // get the rendering context for the map canvas.
  onAdd() {
    const canvas = document.createElement('canvas');
    canvas.width = this.width;
    canvas.height = this.height;
    // @ts-expect-error -- legacy code
    this.context = canvas.getContext('2d');
  },

  // Call once before every frame where the icon will be used.
  render() {
    // @ts-expect-error -- legacy code
    const { context } = this;
    context.clearRect(0, 0, this.width, this.height);
    context.beginPath();

    const duration = 1000;
    const t = (performance.now() % duration) / duration;

    // Only draw
    if (t < lastT) {
      pulseCount += 1;
    }
    lastT = t;

    if (pulseCount % 3 === 0) {
      // Only show the pulse every 5 times
      const radius = (pulseSize / 2) * 0.15;
      const outerRadius = (pulseSize / 2) * 0.7 * t + radius;

      // Draw the outer circle.
      context.arc(this.width / 2, this.height / 2, outerRadius, 0, Math.PI * 2);
      context.fillStyle = `rgba(${pulseColor}, ${1 - t})`;
      context.fill();

      // Draw the inner circle.
      context.beginPath();

      context.fillStyle = `rgba('${pulseColor}', 0.7)`;
      context.strokeStyle = 'white';
      context.lineWidth = 2 + 4 * (1 - t);
      context.fill();
      context.stroke();
    }

    if (pulseColor === PULSE_COLOR_CODES.BLUE) {
      // Draw the inner circle.
      context.beginPath();
      context.arc(this.width / 2, this.height / 2, 20, 0, Math.PI * 2);
      context.fillStyle = `rgba(35, 132, 211, 1)`;
      context.strokeStyle = 'white';
      context.lineWidth = 5;
      context.fill();
      context.stroke();
    }

    // Update this image's data with data from the canvas.
    // The offsets are here to place the pulse behind the icon
    this.data = context.getImageData(
      offsetX,
      offsetY,
      this.width,
      this.height,
    ).data;

    // Continuously repaint the map, resulting
    // in the smooth animation of the dot.
    mapReference.triggerRepaint();

    // Return `true` to let the map know that the image was updated.
    return true;
  },
});

const loadMarkerImages = async (imageIds: string[] = []) => {
  // On-water pulses sit on the point: profile photos are bottom-anchored there
  // and boat icons are centred there. The off-water blue dot keeps its offset.
  if (!mapReference.hasImage('current-location-gold-pulse')) {
    const pulse = await pulsingDot(0, 0, 200, PULSE_COLOR_CODES.GOLD);
    mapReference.addImage('current-location-gold-pulse', pulse);
  }
  if (!mapReference.hasImage('current-location-orange-pulse')) {
    const pulse = await pulsingDot(0, 0, 200, PULSE_COLOR_CODES.ORANGE);
    mapReference.addImage('current-location-orange-pulse', pulse);
  }
  if (!mapReference.hasImage('current-location-blue-pulse')) {
    const pulse = await pulsingDot(0, 25, 200, PULSE_COLOR_CODES.BLUE);
    mapReference.addImage('current-location-blue-pulse', pulse);
  }
  try {
    await ensureBoatTopImages(mapReference);
  } catch (error) {
    telemetry(
      telemetry.WARN,
      `[currentLocationMarkers mapLayer] Failed to load boat icons, ${error}`,
    );
  }

  // Profile photos are the current-location icon when that setting is on.
  // Failed ids are remembered for the session in pin-wrap-images.ts.
  await loadPinWrapImages(mapReference, imageIds);
};
// eslint-disable-next-line no-unused-vars
let selectedCurrentLocation: { latitude: unknown; longitude: unknown } | null;
let selectedHeading: number | null | undefined;
let selectedWatercraftType: string | null | undefined;

const layerName = 'current-location-pulse';
// Shares the profile marker source. Boats need map rotation alignment, which
// cannot vary per feature, so they get their own layer.
const boatLayerName = 'current-location-boat';
// Tilted (3D) boat frames for a pitched map
const tiltedBoatLayerName = 'current-location-boat-tilted';
const boatFilter = ['==', ['get', 'markerKind'], 'vessel'];
// Hidden when zoomed out, like the profile photo
const boatLayerMinZoom = 3;

const addLayersIfRequired = async () => {
  if (!mapReference) return;

  if (!sourceExists(mapReference, 'current-location-profile-pulse')) {
    mapReference.addSource(
      'current-location-profile-pulse',
      emptyStateDataSource,
    );
  }
  if (!sourceExists(mapReference, 'current-location-blue-pulse')) {
    mapReference.addSource('current-location-blue-pulse', emptyStateDataSource);
  }
  if (!sourceExists(mapReference, 'current-location-orange-pulse')) {
    mapReference.addSource(
      'current-location-orange-pulse',
      emptyStateDataSource,
    );
  }
  if (!sourceExists(mapReference, 'current-location-gold-pulse')) {
    mapReference.addSource('current-location-gold-pulse', emptyStateDataSource);
  }
  if (!layerExists(mapReference, 'current-location-blue-pulse')) {
    mapReference.addLayer({
      id: 'current-location-blue-pulse',
      type: 'symbol',
      source: 'current-location-blue-pulse',
      layout: {
        'icon-image': 'current-location-blue-pulse',
        'icon-size': [
          'interpolate',
          ['linear'],
          ['zoom'],
          2.9999,
          ['case', ['==', ['get', 'markerSelected'], true], 0.6, 0],
          3,
          ['case', ['==', ['get', 'markerSelected'], true], 0.6, 0.4],
          8.5,
          ['case', ['==', ['get', 'markerSelected'], true], 0.6, 0.4],
          11,
          ['case', ['==', ['get', 'markerSelected'], true], 0.6, 0.4],
          14,
          ['case', ['==', ['get', 'markerSelected'], true], 0.6, 0.4],
        ],
        'icon-anchor': 'center',
        'icon-allow-overlap': true,
        'text-allow-overlap': true,
      },
      paint: {},
    });
  }
  if (!layerExists(mapReference, 'current-location-orange-pulse')) {
    mapReference.addLayer({
      id: 'current-location-orange-pulse',
      type: 'symbol',
      source: 'current-location-orange-pulse',
      layout: {
        'icon-image': 'current-location-orange-pulse',
        'icon-size': [
          'interpolate',
          ['linear'],
          ['zoom'],
          2.9999,
          ['case', ['==', ['get', 'markerSelected'], true], 0.6, 0],
          3,
          ['case', ['==', ['get', 'markerSelected'], true], 0.6, 0.4],
          8.5,
          ['case', ['==', ['get', 'markerSelected'], true], 0.6, 0.4],
          11,
          ['case', ['==', ['get', 'markerSelected'], true], 0.6, 0.4],
          14,
          ['case', ['==', ['get', 'markerSelected'], true], 0.6, 0.4],
        ],
        'icon-anchor': 'center',
        'icon-allow-overlap': true,
        'text-allow-overlap': true,
      },
      paint: {},
    });
  }

  if (!layerExists(mapReference, 'current-location-gold-pulse')) {
    mapReference.addLayer({
      id: 'current-location-gold-pulse',
      type: 'symbol',
      source: 'current-location-gold-pulse',
      layout: {
        'icon-image': 'current-location-gold-pulse',
        'icon-size': [
          'interpolate',
          ['linear'],
          ['zoom'],
          2.9999,
          ['case', ['==', ['get', 'markerSelected'], true], 0.6, 0],
          3,
          ['case', ['==', ['get', 'markerSelected'], true], 0.6, 0.4],
          8.5,
          ['case', ['==', ['get', 'markerSelected'], true], 0.6, 0.4],
          11,
          ['case', ['==', ['get', 'markerSelected'], true], 0.6, 0.4],
          14,
          ['case', ['==', ['get', 'markerSelected'], true], 0.6, 0.4],
        ],
        'icon-anchor': 'center',
        'icon-allow-overlap': true,
        'text-allow-overlap': true,
      },
      paint: {},
    });
  }
  if (!layerExists(mapReference, boatLayerName)) {
    mapReference.addLayer({
      id: boatLayerName,
      type: 'symbol',
      source: 'current-location-profile-pulse',
      minzoom: boatLayerMinZoom,
      filter: boatFilter,
      layout: boatIconLayout,
      paint: {},
    });
  }
  if (!layerExists(mapReference, tiltedBoatLayerName)) {
    mapReference.addLayer({
      id: tiltedBoatLayerName,
      type: 'symbol',
      source: 'current-location-profile-pulse',
      minzoom: boatLayerMinZoom,
      layout: tiltedBoatIconLayout,
      paint: {},
    });
    registerBoatLayers(mapReference, {
      sourceId: 'current-location-profile-pulse',
      topLayerId: boatLayerName,
      tiltedLayerId: tiltedBoatLayerName,
      filter: boatFilter,
    });
  }
  if (!layerExists(mapReference, 'current-location-profile-pulse')) {
    mapReference.addLayer({
      id: 'current-location-profile-pulse',
      type: 'symbol',
      source: 'current-location-profile-pulse',
      filter: ['==', ['get', 'markerKind'], 'profile'],
      layout: {
        'icon-image': ['to-string', ['get', 'image']],
        'icon-size': [
          'interpolate',
          ['linear'],
          ['zoom'],
          2.9999,
          ['case', ['==', ['get', 'markerSelected'], true], 0.6, 0],
          3,
          ['case', ['==', ['get', 'markerSelected'], true], 0.6, 0.4],
          8.5,
          ['case', ['==', ['get', 'markerSelected'], true], 0.6, 0.4],
          11,
          ['case', ['==', ['get', 'markerSelected'], true], 0.6, 0.4],
          14,
          ['case', ['==', ['get', 'markerSelected'], true], 0.6, 0.4],
        ],
        'icon-anchor': 'bottom',
        'icon-allow-overlap': true,
        'text-allow-overlap': true,
      },
      paint: {},
    });
  }
};

const layer: { [key: string]: unknown } = {
  displayName: 'Current Location',
  synonyms: '',
  group: '',
  typeUrl: '',
  icon: {
    baseShape: vesselIconSpeedBoat,
    iconColor: '#fff',
    markerColor: '#139ECA',
  },
  shownByDefault: true,
  relativeDepth: 0,
  menuOptions: {
    visible: false,
    owningMenu: 'marine-points-of-interest',
    order: 0,
  },
};

const toggleActiveLayer = (
  color: string,
  geoData: any,
  showProfile: boolean,
) => {
  const colors = ['orange', 'blue', 'gold'];

  // Toggling Active Pulse
  toggleLayerIfRequired(mapReference, `current-location-${color}-pulse`, true);
  setDataSource(mapReference, `current-location-${color}-pulse`, geoData);

  // Toggling Profile, or the boat when not showing a profile photo
  toggleLayerIfRequired(
    mapReference,
    `current-location-profile-pulse`,
    showProfile,
  );
  toggleLayerIfRequired(mapReference, boatLayerName, showProfile);
  toggleLayerIfRequired(mapReference, tiltedBoatLayerName, showProfile);
  setBoatSourceData(
    mapReference,
    `current-location-profile-pulse`,
    showProfile ? geoData : emptyStateDataSource.data,
  );

  // Disabling others
  colors
    .filter((selectedColor) => selectedColor !== color)
    .forEach((selectedColor) => {
      toggleLayerIfRequired(
        mapReference,
        `current-location-${selectedColor}-pulse`,
        false,
      );
      setDataSource(
        mapReference,
        `current-location-${selectedColor}-pulse`,
        emptyStateDataSource.data,
      );
    });
};

// Selected vessel, otherwise the first vessel. The saved signal carries the
// first watercraft too, for when the vessel list has not been fetched.
const currentWatercraftType = (state: any): string | null | undefined =>
  selectSelectedOrFirstVessel(state)?.watercraft_type ??
  getSignalForCurrentMember(state)?.watercraft_type;

// Live device heading (null when not moving), otherwise the saved signal heading
const currentHeading = (state: any): number | null | undefined =>
  state.geoService.currentHeading ?? getSignalForCurrentMember(state)?.heading;

export const updateData = async (): Promise<void> => {
  // If we have yet to receive a reference to the map object through the onInitialize function,
  // just return as we can't update the data on the layer if the layer doesn't exist yet.
  if (!mapReference) return;

  addLayersIfRequired();

  const state = getStore().getState();

  const { lat, lon } = state.geoService.currentPosition;
  const { image } = state.backendService.currentMember || {};
  const isMemberSubscribed = selectHasCurrentMemberActiveSubscription(state);
  const { signalMapSetting } = selectLocationConfig(state);
  const isOnWater = getOnTheWaterFromState(state);

  const currentPulseColor = () => {
    if (!isOnWater || signalMapSetting === SignalMapSetting.Invisible)
      return 'blue';
    if (isOnWater && isMemberSubscribed) return 'gold';
    if (isOnWater && !isMemberSubscribed) return 'orange';
    return 'blue';
  };

  const showProfile =
    image && signalMapSetting === SignalMapSetting.Profile && isOnWater;
  const showImageInMarker = Boolean(
    (signalMapSetting === SignalMapSetting.Profile ||
      signalMapSetting === SignalMapSetting.Anonymous) &&
    isOnWater,
  );
  await loadMarkerImages(showProfile ? [image] : []);

  // Store this in meta store to turn on/off broadly for debugging
  if (window?.DECKEE_CONFIG && window?.DECKEE_CONFIG?.trackBlueDotUpdates) {
    captureEvent({
      type: 'app: blue-dot-update',
      payload: {
        lat,
        lon,
      },
    });
  }

  const geojson = {
    type: 'FeatureCollection',
    features: [
      {
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [lon, lat],
        },
        properties: showProfile
          ? { image, markerKind: 'profile' }
          : {
              image: boatIconForWatercraftType(currentWatercraftType(state)),
              markerKind: 'vessel',
              heading: normalizeHeading(currentHeading(state)),
            },
      },
    ],
  };

  toggleActiveLayer(currentPulseColor(), geojson, showImageInMarker);
};
layer.onInitialize = (map: any) => {
  // eslint-disable-next-line no-multi-assign
  mapReference = map;
  addLayersIfRequired();
  watchTiltedBoatView(map);

  map.on('deckee-press', layerName, async (event: any) => {
    event.originalEvent.preventDefault();
  });

  getStore().subscribe(() => {
    const state = getStore().getState();

    const currentPosition = selectCurrentLocation(state);
    const heading = currentHeading(state);
    const watercraftType = currentWatercraftType(state);

    if (
      selectedCurrentLocation !== currentPosition ||
      selectedHeading !== heading ||
      selectedWatercraftType !== watercraftType
    ) {
      selectedCurrentLocation = currentPosition;
      selectedHeading = heading;
      selectedWatercraftType = watercraftType;
      updateData();
    }
  });
};

layer.onToggle = (map: any, show: boolean) => {
  toggleLayerIfRequired(map, layerName, show);
};

export default layer;
